(function () {
  const TIER_WRAPPER = ".lb-qty-selector-tier-wrapper";
  const TIER_INPUT = ".lb-qty-selector-tier-input";
  const TIER_CONTAINER = ".lb-qty-selector-tier-container";

  // Elementi generati dall'app: unico perimetro su cui interveniamo
  const APP_NODES = '[class*="lb-qty-selector"]';

  // Elementi il cui ruolo implicito e' generico o nullo: qualunque stato o
  // proprieta' ARIA che richieda un ruolo, qui, non e' supportato
  const GENERIC_TAGS = new Set([
    "DIV",
    "SPAN",
    "LABEL",
    "P",
    "SECTION",
    "B",
    "I",
    "EM",
    "STRONG",
    "SMALL",
  ]);

  /*
    Attributi ARIA globali: ammessi su qualunque elemento, indipendentemente
    dal ruolo (WAI-ARIA 1.2, "Global States and Properties").

    Tutto il resto degli aria-* esiste solo in presenza di un ruolo che lo
    supporti, quindi su un elemento a ruolo generico e' una violazione
    aria-allowed-attr. Ragionare per allowlist invece che per lista di
    attributi vietati e' l'unico modo di coprire anche gli attributi che
    l'app potrebbe aggiungere in futuro: la lista chiusa precedente lasciava
    passare tutto cio' che non era stato previsto.

    aria-label, aria-labelledby e aria-description NON sono nell'allowlist pur
    essendo globali: su ruolo generico o assente sono prohibited attributes
    (axe: aria-prohibited-attr) e vengono ignorati nel calcolo del nome
    accessibile.
  */
  const GLOBAL_ARIA = new Set([
    "aria-atomic",
    "aria-busy",
    "aria-controls",
    "aria-current",
    "aria-describedby",
    "aria-details",
    "aria-flowto",
    "aria-hidden",
    "aria-keyshortcuts",
    "aria-live",
    "aria-owns",
    "aria-relevant",
    "aria-roledescription",
  ]);

  let scheduled = false;

  /**
   * Fix accessibilita' tier Dealeasy.
   *
   * L'app genera <label role="radio" aria-label="Tier N"> con dentro un
   * <input type="radio" aria-hidden tabindex="-1">.
   * role="radio" rende presentazionali i figli del label: un input nativo resta
   * comunque focalizzabile (tabindex="-1" non lo esclude per axe/eye-able) e
   * produce un tab stop vuoto -> violazione "nested-interactive" /
   * "presentational children have no focusable content".
   *
   * Non si puo' rendere l'input non focalizzabile (disabled blocca il click sul
   * label, display:none elimina il pallino stilizzato dalla CSS del tema),
   * quindi si rimuove il ruolo ARIA ridondante e si restituisce la semantica
   * nativa: gli input con lo stesso name formano gia' un radio group
   * accessibile, con selezione da tastiera tramite frecce.
   */
  const restoreNativeRadios = () => {
    document.querySelectorAll(TIER_WRAPPER).forEach((wrapper) => {
      const input = wrapper.querySelector('input[type="radio"]');
      if (!input) return;

      // Il label torna un label: nessun ruolo che renda presentazionali i figli
      wrapper.removeAttribute("role");
      wrapper.removeAttribute("aria-checked");
      wrapper.removeAttribute("tabindex");
      // aria-label="Tier 1" prevarrebbe sul testo visibile nel calcolo del
      // nome accessibile dell'input: si lascia parlare il contenuto del label
      wrapper.removeAttribute("aria-label");

      // L'input torna il radio reale: visibile alle AT e raggiungibile da tastiera
      input.removeAttribute("aria-hidden");
      input.removeAttribute("tabindex");

      // Associazione esplicita label/input se l'app non l'ha impostata
      if (input.id && wrapper.getAttribute("for") !== input.id) {
        wrapper.setAttribute("for", input.id);
      }
    });
  };

  /**
   * Rimuove gli attributi ARIA non supportati dal ruolo dell'elemento che li
   * porta (axe: aria-allowed-attr / aria-prohibited-attr).
   *
   * L'app riscrive aria-checked sul label a ogni selezione e distribuisce
   * aria-label sui div interni: senza role="radio" quegli attributi non hanno
   * un ruolo che li esponga, quindi vengono ignorati dalle tecnologie
   * assistive e segnalati come non conformi. Si interviene solo su elementi
   * privi di role esplicito e con ruolo implicito generico, per non toccare
   * ARIA legittima eventualmente aggiunta dall'app.
   */
  const stripUnsupportedAria = (el) => {
    // aria-hidden="" e' un valore non valido: l'app lo genera cosi'
    if (el.getAttribute("aria-hidden") === "") {
      el.setAttribute("aria-hidden", "true");
    }

    if (el.hasAttribute("role")) return;
    if (!GENERIC_TAGS.has(el.tagName)) return;

    // Array.from perche' el.attributes e' una collezione live: rimuovere
    // durante l'iterazione diretta salterebbe gli attributi successivi.
    Array.from(el.attributes).forEach(({ name }) => {
      if (!name.startsWith("aria-")) return;
      if (GLOBAL_ARIA.has(name)) return;
      el.removeAttribute(name);
    });
  };

  const sanitizeUnsupportedAria = () => {
    document.querySelectorAll(APP_NODES).forEach(stripUnsupportedAria);
  };

  /**
   * Nome accessibile del gruppo (WCAG 1.3.1, axe: aria-required-attr su
   * radiogroup / "form elements must have a visible label").
   *
   * L'app genera <div class="lb-qty-selector-tier-container" role="radiogroup">
   * senza aria-label ne' aria-labelledby: il gruppo esiste per le tecnologie
   * assistive ma resta senza nome, quindi lo screen reader annuncia solo
   * "gruppo di pulsanti di opzione" senza dire di cosa si tratta. Non e'
   * convertibile in <fieldset><legend>, il markup e' dell'app e viene
   * ri-renderizzato al cambio variante.
   *
   * Se l'app rende un titolo visibile lo si usa come aria-labelledby, cosi' il
   * nome accessibile coincide con quello visibile; altrimenti si ricade su
   * aria-label con la stringa localizzata del tema.
   */
  const nameTierGroups = () => {
    document.querySelectorAll(TIER_CONTAINER).forEach((group, index) => {
      // Il ruolo lo mette l'app, ma non e' garantito su tutte le sue versioni
      if (!group.getAttribute("role")) {
        group.setAttribute("role", "radiogroup");
      }

      const hasName =
        (group.getAttribute("aria-label") || "").trim() ||
        group.getAttribute("aria-labelledby");
      if (hasName) return;

      const heading = group.parentElement?.querySelector(
        '[class*="lb-qty-selector"][class*="title"], [class*="lb-qty-selector"][class*="heading"]',
      );

      if (heading && (heading.textContent || "").trim()) {
        if (!heading.id) {
          heading.id = `lb-qty-selector-tier-title-${index}`;
        }
        group.setAttribute("aria-labelledby", heading.id);
        return;
      }

      const label =
        window.FoxTheme?.accessibilityStrings?.quantityTiersGroup ||
        "Volume pricing";
      group.setAttribute("aria-label", label);
    });
  };

  const fixAccessibility = () => {
    if (!document.querySelector(APP_NODES)) return;
    restoreNativeRadios();
    sanitizeUnsupportedAria();
    // Dopo sanitizeUnsupportedAria: quella funzione rimuove aria-label dagli
    // elementi generici senza ruolo, quindi il nome va assegnato per ultimo,
    // insieme al role che lo rende valido.
    nameTierGroups();
  };

  const scheduleFix = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      // runFix e non fixAccessibility: i container appena montati dall'app
      // vanno anche messi sotto osservazione. Definita piu' sotto, ma il
      // riferimento viene risolto solo allo scadere del rAF.
      runFix();
    });
  };

  /*
    Due osservatori con compiti distinti.

    1. Sul body, solo childList: intercetta i tier montati o ri-renderizzati
       dall'app (cambio variante, quick view) e programma il fix su rAF. Non
       osserva gli attributi: su document.body con subtree significherebbe
       essere richiamati per ogni cambio di class o style del tema.

    2. Su ciascun container dei tier, tutti gli attributi senza filtro e in
       modo SINCRONO. L'app riscrive aria-checked e role sul label a ogni
       selezione: con il solo debounce su rAF l'attributo non valido restava
       nel DOM per un frame, e una scansione fatta in quell'istante lo
       segnalava comunque. Senza attributeFilter copre anche gli aria-*
       che non sappiamo in anticipo, coerentemente con l'allowlist.

    fixAccessibility solo rimuove e normalizza, quindi e' idempotente: le
    mutazioni che genera fanno scattare l'osservatore un'ultima volta, non
    trovano nulla da cambiare e la catena si chiude.
  */
  const observedContainers = new WeakSet();

  const observeContainers = () => {
    document.querySelectorAll(TIER_CONTAINER).forEach((group) => {
      if (observedContainers.has(group)) return;
      observedContainers.add(group);

      new MutationObserver(fixAccessibility).observe(group, {
        attributes: true,
        subtree: true,
      });
    });
  };

  const runFix = () => {
    fixAccessibility();
    observeContainers();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", runFix);
  } else {
    runFix();
  }

  new MutationObserver(scheduleFix).observe(document.body, {
    childList: true,
    subtree: true,
  });

  const getQtyFromWrapper = (wrapper) => {
    const qtyText =
      wrapper.querySelector(".lb-qty-selector-quantity-text")?.textContent || "";
    const qty = parseInt(qtyText, 10);

    // "1 Pezzo" -> 1, "4+ pezzi" -> 4 (soglia minima del tier)
    return Number.isInteger(qty) ? qty : null;
  };

  const updateThemeQty = (qty) => {
    const defaultQtyInput = document.querySelector(
      "quantity-input input[type='number']",
    );
    if (!defaultQtyInput) return;

    // Guardia di idempotenza: click e change possono arrivare entrambi
    if (String(defaultQtyInput.value) === String(qty)) return;

    defaultQtyInput.value = qty;
    defaultQtyInput.dispatchEvent(new Event("input", { bubbles: true }));
    defaultQtyInput.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const syncFromWrapper = (wrapper) => {
    if (!wrapper) return;
    const qty = getQtyFromWrapper(wrapper);
    if (qty == null) return;
    updateThemeQty(qty);
  };

  // change: copre selezione da tastiera (frecce) e click sul label
  document.addEventListener("change", (event) => {
    const input = event.target.closest?.(TIER_INPUT);
    if (!input) return;
    syncFromWrapper(input.closest(TIER_WRAPPER));
  });

  // click: fallback se l'app intercetta l'evento e gestisce lo stato da se'
  document.addEventListener("click", (event) => {
    syncFromWrapper(event.target.closest?.(TIER_WRAPPER));
  });
})();
